import React from 'react';
import { inject, observer } from 'mobx-react';
import autobind from 'autobind';
import { sha256 } from 'js-sha256';
import { action, makeObservable, observable } from 'mobx';
import { withTranslation, WithTranslation } from 'react-i18next';

import '@components/Print/Print_.scss';

import { EntryStatus, EntryTypeFieldType, EntryTypeType, IndividualReportType } from 'chaika-enums';

import { isDefined } from '@utils/isDefined';
import { assertIsDefined } from '@utils/assertIsDefined';

import { EntryApi } from '@api/EntryApi';
import { NotificationApi } from '@api/NotificationApi';
import { ZebraApi } from '@api/ZebraApi';

import { translate } from '@chaika/i18n/helper';

import { Ehr } from '@patient/store/ehr/Ehr';
import { Entry } from '@patient/store/entry/Entry';
import { entryService } from '@patient/services/entry/entryService';
import { AuthUser } from '@store/AuthUser/AuthUser';
import { AppState, SimplePopupParams } from '@store/App/App';
import { ServiceStore } from '@services/store/service/ServiceStore';
import { ENTRY_FILE_DEFAULT_NAME, SERVICE_WITH_CONCLUSION_ID } from '@store/types';

import { EntryPage as EntryPageStore, entryPageService } from '@patient/ui/shared/store';
import { ActionButtonContent, ActionButtonContentParams } from './ActionButton';

import { ViewPortState } from '@store/ViewPort';
import { RenderedServiceList as RenderedServiceListStore } from '@store/models/SystemFields/RenderedService';
import { SelectButtonListModifiers, SelectButtonMenuItem } from '@components/SelectButtonList/SelectButtonList';
import { EntryPageTestAttributes } from 'chaika-test-attributes';
import { EntryConfirmPopup } from '@components/Popup/EntryConfirmPopup';
import { entryMedicalCaseService } from '@modules/patient/services/entry/entryMedicalCaseService';
import { IndividualReportsStore } from '@modules/organization/ui/LegalEntityPage/pages/IndividualReportsPage/store/IndividualReportsStore';
import { LegalEntityBranchList } from '@modules/organization/store/legalEntityBranch/LegalEntityBranchList';
import { IndividualReportsPrintPopup } from '@common/IndividualReportsPrintPopup';
import { EditEntryData } from '@common/EditEntryData';
import { RightContent } from '@patient/ui/shared/RightContent';
import { Print } from '@components/Print';
import { ENTRY_CONTENT_IN_VIEW_MODE_IDENTIFIER } from '@patient/store/entry/constants';
import { ExaminationLink } from '@patient/ui/shared/ExaminationLink/ExaminationLink';
import { entryIndividualPrintFormService } from '@patient/services/entry/entryIndividualPrintFormService';
import { EhrApi, FileApi } from '@api';

export interface ActionButtonProps extends ActionButtonContentParams {
    onAddNestedEntryClick(): void;
    onSendIDSClick(printMode?: boolean): void;
}

interface StoreProps {
    serviceStore: ServiceStore;
    ehr: Ehr;
    entry: Entry;
    app: AppState;
    entryPage: EntryPageStore;
    authUser: AuthUser;
    viewPort: ViewPortState;
    individualReportsStore: IndividualReportsStore;
    legalEntityBranchList: LegalEntityBranchList;
}

export enum Mode {
    CanEdit = 'canEdit',
    CanPublish = 'canPublish',
    CanDescribe = 'canDescribe',
    CanSetReadyForDescribingStatus = 'canSetReadyForDescribingStatus',
    OnlyView = 'onlyView',
}

const PRINTED_ENTRY_CONTENT_MESSAGE = 'Entry content is rendered on the entry page while it is printed';

const injectingStores: (keyof StoreProps)[] = [
    ServiceStore.Name,
    Ehr.Name,
    EntryPageStore.Name,
    Entry.Name,
    AuthUser.Name,
    AppState.Name,
    ViewPortState.Name,
    IndividualReportsStore.Name,
    LegalEntityBranchList.Name,
];

@observer
class Component extends React.Component<ActionButtonProps & StoreProps & WithTranslation> {
    @observable private isShowingEmailInputPopup: boolean = false;
    @observable private isShowingEDSPopup: boolean = false;
    @observable private email?: string;

    private EntryTitles = {
        Analysis: translate('analyzes').toLowerCase(),
        Covid: translate('covidTesting'),
    };

    constructor(props: ActionButtonProps & StoreProps & WithTranslation) {
        super(props);
        makeObservable(this);
    }

    public render(): React.ReactNode {
        const {
            modifiers,
            viewPort: { isMobileSize },
            entry: { isViewMode },
        } = this.props;

        return React.createElement(ActionButtonContent, {
            modifiers,
            isViewMode,
            isMobile: isMobileSize,
            items: this.getMenuItems(),
            title: this.getTitle(),
            isShowingEmailInputPopup: this.isShowingEmailInputPopup,
            isShowingEDSPopup: this.isShowingEDSPopup,
            email: this.email,
            primaryActionDisabled: false,
            onEmailInput: this.handleEmailInput,
            onConfirmEmailSending: this.proceedSendToEmail,
            onCancelEmailSending: this.handleCancelEmailSending,
            onButtonClick: this.handleOnButtonClick,
            onCloseEDSPopup: this.toggleEDSPopup,
        });
    }

    private getTitle(): string {
        const mode = this.getMode();
        return translate(mode);
    }

    private getMode(): Mode {
        const { entry, authUser } = this.props;
        const isSigned = entry.status === EntryStatus.Signed;
        const isReadyForDescribing = entry.status === EntryStatus.ReadyForDescribing;
        const isDescribeNeeded = entry.isDescribeNeeded;
        const isCanEditAndSign = entry.isCanEditAndSign(authUser.id);
        const hasCoAuthors = !!entry.coAuthorIds?.length;

        let mode: Mode;

        if (isDescribeNeeded && (isCanEditAndSign || authUser.hasRootAccess || authUser.isAssistant)) {
            mode = Mode.CanSetReadyForDescribingStatus;
        } else if (isReadyForDescribing && (isCanEditAndSign || authUser.hasRootAccess || authUser.isAssistant)) {
            mode = Mode.CanPublish;
        } else if (isReadyForDescribing && !hasCoAuthors) {
            mode = Mode.CanDescribe;
        } else if (isCanEditAndSign || authUser.hasRootAccess || authUser.isAssistant) {
            mode = isSigned ? Mode.CanEdit : Mode.CanPublish;
        } else {
            mode = Mode.OnlyView;
        }

        return mode;
    }

    private getMenuItems() {
        const {
            entry,
            authUser,
            modifiers,
            viewPort: { isMobileSize },
            individualReportsStore,
            legalEntityBranchList,
        } = this.props;
        const items: SelectButtonMenuItem[] = [];

        const mode = this.getMode();

        const legalEntityId = legalEntityBranchList.getActualOnDayLegalEntityId(
            entry.organizationId,
            entry.creationTime,
        );
        const hasIndividualReports = individualReportsStore.hasActiveIndividualReports({
            legalEntityId,
            type: IndividualReportType.Entry,
        });

        const entryTitle = entry.entryType.title.toLowerCase();
        let showConclusion = false;

        if (entryTitle === this.EntryTitles.Covid) {
            showConclusion = true;
        } else if (entry.hasRenderedServices && entryTitle === this.EntryTitles.Analysis) {
            const renderedServiceValue = entry.fields
                .get(EntryTypeFieldType.RenderedService)
                .getValue() as RenderedServiceListStore;
            showConclusion = !!renderedServiceValue.values.find(
                value => value.serviceId === SERVICE_WITH_CONCLUSION_ID,
            );
        }

        if (mode !== Mode.OnlyView) {
            if (isMobileSize && modifiers?.includes(SelectButtonListModifiers.Header)) {
                items.push({
                    label: translate('publish'),
                    onClick: this.handleOnButtonClick,
                    dataTest: EntryPageTestAttributes.SignButton,
                });
            }

            items.push({
                label: translate('print'),
                dataTest: EntryPageTestAttributes.Print,
                onClick: this.entryPrint,
            });

            if (entry.isSigned) {
                items.push({
                    label: translate('signEDS'),
                    disable: isDefined(entry.eds),
                    dataTest: EntryPageTestAttributes.SignEDS,
                    onClick: this.toggleEDSPopup,
                });
            }
        }

        if (entry.entryType.type === EntryTypeType.Consultation) {
            items.push({
                label: translate('shortConclusion'),
                dataTest: EntryPageTestAttributes.ShortConclusion,
                onClick: this.handleShortPrint,
            });
        }
        if (entry.isSigned && showConclusion) {
            items.push({
                label: translate('conclusion'),
                dataTest: EntryPageTestAttributes.Conclusion,
                onClick: this.handleDownloadConclusion,
            });
        }

        if (entry.entryType.type === EntryTypeType.Tests) {
            items.push({
                label: translate('printLabel'),
                dataTest: EntryPageTestAttributes.PrintLabel,
                onClick: this.handlePrintLabel,
            });
        }

        if (legalEntityId && hasIndividualReports) {
            items.push({
                label: translate('printIndividualReport'),
                dataTest: EntryPageTestAttributes.PrintIndividualReport,
                onClick: () => this.handlePrintIndividualReport(legalEntityId),
            });
        }

        if (
            (authUser.hasRootAccess || authUser.isAssistant) &&
            [EntryTypeType.Diagnostics, EntryTypeType.UsDiagnostics].includes(entry.type.type)
        ) {
            items.push({
                label: translate('addExaminationLink'),
                dataTest: EntryPageTestAttributes.AddExaminationLink,
                onClick: () => this.onAddExaminationLink(),
            });
        }

        items.push(
            {
                label: translate('changeEntryData'),
                dataTest: EntryPageTestAttributes.ChangeEntryData,
                onClick: this.handleEditEntryData,
            },
            {
                label: translate('sendEmail'),
                dataTest: EntryPageTestAttributes.SendEmail,
                onClick: this.handleSendToEmail,
            },
            {
                label: translate('fillingHistory'),
                dataTest: EntryPageTestAttributes.Versions,
                onClick: this.handleVersionClick,
            },
        );

        items.push({
            label: translate('translationEntry'),
            dataTest: EntryPageTestAttributes.TranslationEntry,
            onClick: this.handleTranslationClick,
        });

        return items;
    }

    @autobind
    private onAddExaminationLink(): void {
        entryPageService.setRightContent({
            children: React.createElement(ExaminationLink),
            onClickOutside: () => entryPageService.resetRightContent(),
        });
    }

    @autobind
    private handleTranslationClick(): void {
        this.props.entry.isTranslationMode = true;
    }

    @autobind
    private handleVersionClick(): void {
        this.props.entryPage.onClickOutsideRightContent = entryPageService.resetRightContent;
        entryPageService.setRightContent({
            children: React.createElement(RightContent),
            isShowing: true,
        });
    }

    @autobind
    private async handleOnButtonClick() {
        const { entry, authUser } = this.props;
        const mode = this.getMode();

        switch (mode) {
            case Mode.OnlyView:
                await this.handlePrint();
                break;
            case Mode.CanSetReadyForDescribingStatus:
                await entryService.setReadyForDescribingStatus();
                break;
            case Mode.CanDescribe:
                await this.handleAddCoAuthor();
                entry.coAuthorIds = [authUser.id];
                entry.isViewMode = false;
                break;
            case Mode.CanPublish:
                await this.signEntry();
                break;
            case Mode.CanEdit:
                await this.editEntry();
                break;
        }
    }

    private async signEntry(): Promise<void> {
        const { app, entry } = this.props;

        const tShowNoEpisodePopupTitle = translate('showNoEpisodePopupTitle');
        const tShowNoReferralsPopupTitle = translate('showNoReferralsPopupTitle');
        const tShowNoTasksPopupTitle = translate('showNoTasksPopupTitle');
        const tShowNoDiagnosesPopupTitle = translate('addDiagnosis');
        const tShowNoRenderedServicesPopupTitle = translate('showNoRenderedServicesPopupTitle');

        const tShowNoMedicalCasesPopupDescription = translate('showNoMedicalCasesPopupDescription');
        const tShowNoReferralsPopupDescription = translate('showNoReferralsPopupDescription');
        const tShowNoTasksPopupDescription = translate('showNoTasksPopupDescription');
        const tShowNoDiagnosesPopupDescription = translate('showNoDiagnosesPopupDescription');
        const tHasRenderedServicesDescription = translate('hasRenderedServicesDescription');

        const tHasRenderedServicesConfirm = translate('hasRenderedServicesConfirm');
        const tConfirmWithoutReferrals = translate('confirmWithoutReferrals');
        const tConfirmWithoutTasks = translate('confirmWithoutTasks');
        const tConfirm = translate('continue');
        const tPopupCancel = translate('returnToEntry');

        const commonPopupParams: Partial<SimplePopupParams> = {
            withoutCloseButton: true,
            customWidth: true,
            modifiers: [],
        };

        if (entry.showNoMedicalCasesPopup) {
            app.showPopup({
                ...commonPopupParams,
                getDescription: () =>
                    React.createElement(EntryConfirmPopup, {
                        withBadge: true,
                        title: tShowNoEpisodePopupTitle,
                        description: tShowNoMedicalCasesPopupDescription,
                        cancelText: tPopupCancel,
                        onCancel: this.handleClosePopup,
                    }),
            });
        } else if (entry.showNoDiagnosesPopup) {
            app.showPopup({
                ...commonPopupParams,
                getDescription: () =>
                    React.createElement(EntryConfirmPopup, {
                        withBadge: true,
                        title: tShowNoDiagnosesPopupTitle,
                        description: tShowNoDiagnosesPopupDescription,
                        cancelText: tPopupCancel,
                        onCancel: this.handleClosePopup,
                    }),
            });
        } else if (!entryMedicalCaseService.everyMedicalCaseValidForSign()) {
            const titles: string[] = [];
            const descriptions: string[] = [];
            let confirmTexts: string[] = [];

            const isReferralsCheckValid = entryMedicalCaseService.everyMedicalCaseReferralsCheck();
            const isTasksCheckValid = entryMedicalCaseService.everyMedicalCaseTasksCheck();

            if (!isReferralsCheckValid) {
                titles.push(tShowNoReferralsPopupTitle);
                descriptions.push(tShowNoReferralsPopupDescription);
                confirmTexts.push(tConfirmWithoutReferrals);
            }

            if (!isTasksCheckValid) {
                titles.push(tShowNoTasksPopupTitle);
                descriptions.push(tShowNoTasksPopupDescription);
                confirmTexts.push(tConfirmWithoutTasks);
            }

            if (!isReferralsCheckValid && !isTasksCheckValid) {
                confirmTexts = [tConfirm];
            }

            app.showPopup({
                ...commonPopupParams,
                getDescription: () =>
                    React.createElement(EntryConfirmPopup, {
                        title: titles.join('. '),
                        description: descriptions.join(' '),
                        confirmText: confirmTexts.join(' '),
                        withBadge: true,
                        cancelText: tPopupCancel,
                        onConfirm: this.handleNoReferralsConfirm,
                        onCancel: this.handleCloseNoReferralsPopup,
                    }),
            });
        } else if (!entry.hasRenderedServices) {
            app.showPopup({
                ...commonPopupParams,
                getDescription: () =>
                    React.createElement(EntryConfirmPopup, {
                        withBadge: true,
                        title: tShowNoRenderedServicesPopupTitle,
                        description: tHasRenderedServicesDescription,
                        confirmText: tHasRenderedServicesConfirm,
                        cancelText: tPopupCancel,
                        onConfirm: this.proceedSigningEntry,
                        onCancel: this.handleClosePopup,
                    }),
            });
        } else {
            await this.proceedSigningEntry();
        }
    }

    private async editEntry(): Promise<void> {
        const {
            entry,
            entryPage: { rightMenu },
            app,
        } = this.props;
        app.setIsOnUpdate(true);
        entry.translations.resetStatus();
        await entryService.setDraftStatus();

        rightMenu.signedMode = false;
        entry.isViewMode = false;
        app.setIsOnUpdate(false);
    }

    @autobind
    private async handleNoReferralsConfirm(): Promise<void> {
        entryMedicalCaseService.makeAllReferralsOptional();
        await this.proceedSigningEntry();
    }

    @autobind
    private async proceedSigningEntry(): Promise<void> {
        const { app, entryPage } = this.props;
        app.confirmPopupParams = null;
        this.handleClosePopup();
        app.setIsOnUpdate(true);

        try {
            await entryService.setSignedStatus();
            entryPage.rightMenu.signedMode = true;
        } catch (e: any) {
            app.showingPopupParams = {
                title: translate('errorOnSigning'),
                description: e.message,
            };
        }
        app.setIsOnUpdate(false);
    }

    @autobind
    private handleClosePopup(): void {
        const { app } = this.props;

        app.showPopup(null);
    }

    @autobind
    private handleCloseNoReferralsPopup(): void {
        const { app } = this.props;

        app.showPopup(null);
        entryMedicalCaseService.validateTreatmentPlans();
    }

    @autobind
    private async handlePrintLabel(): Promise<void> {
        const { ehr, app } = this.props;

        const label = await ZebraApi.generateSimpleLabel(ehr).catch(() =>
            app.showPopup({
                title: translate('error'),
                description: translate('failedGenereateLabel'),
            }),
        );

        if (label) {
            await ZebraApi.print(label).catch(() =>
                app.showPopup({
                    title: translate('printError'),
                    description: translate('printerNotFound'),
                }),
            );
        }
    }

    @autobind
    private async handlePrintIndividualReport(legalEntityId: string): Promise<void> {
        const { app, ehr } = this.props;
        const [ehrData] = ehr?.id ? await EhrApi.find({ ids: ehr.id, withTranslations: true }) : [];
        const params = await entryIndividualPrintFormService.makeIndividualReportData(ehrData);

        app.showPopup({
            withoutCloseButton: true,
            modifiers: [],
            description: () =>
                React.createElement(IndividualReportsPrintPopup, {
                    legalEntityId,
                    type: IndividualReportType.Entry,
                    params,
                }),
        });
    }

    private async handleAddCoAuthor() {
        await entryService.addCoAuthor();
    }

    @autobind
    private async handleDownloadConclusion() {
        const {
            entry: { id },
        } = this.props;

        const token = sha256(`${id}/entry/dTlnvwE1eOvVfZ14ya7vvOX3ixuDbKtU`);
        const url = `https://chaika.com/api/v1/mis/entries/${id}/pdf?token=${token}`;

        window.open(url, '_blank');
    }

    @autobind
    private handleEmailInput({ target: { value } }: React.ChangeEvent<HTMLInputElement>): void {
        this.email = value;
    }

    @autobind
    private async handleEditEntryData(): Promise<void> {
        const {
            app,
            entry: { id },
        } = this.props;

        const { entry } = await EntryApi.getEntry(id);

        app.setRightMenu({
            children: React.createElement(EditEntryData, { entry }),
            onClickOutside: this.closeRightMenu,
        });
    }

    @autobind
    private handleSendToEmail(): void {
        const {
            ehr: { email },
        } = this.props;

        this.isShowingEmailInputPopup = true;
        this.email = email;
    }

    @autobind
    private handleCancelEmailSending(): void {
        this.isShowingEmailInputPopup = false;
    }

    @autobind
    private async proceedSendToEmail(): Promise<void> {
        const {
            entry: { id },
        } = this.props;
        assertIsDefined(this.email, 'Send-to-email confirm button is disabled until an email is entered');
        await NotificationApi.sendEntryToEmail({ email: this.email, entryId: id });
        this.handleCancelEmailSending();
    }

    @autobind
    private async handlePrint() {
        await this.print();
    }

    @autobind
    private async entryPrint(): Promise<void> {
        const { files } = this.props.entry;
        const hasEntryFile = files.length > 0;
        const entryFile = hasEntryFile && files.find(it => it.path.includes(ENTRY_FILE_DEFAULT_NAME));

        if (entryFile) {
            await FileApi.showFile(entryFile.path);
        } else {
            await this.print();
        }
    }

    @autobind
    private async handleShortPrint() {
        await this.print(true);
    }

    private async print(isShortPrintForm: boolean = false): Promise<void> {
        const { entry, app, entryPage } = this.props;
        const { isViewMode } = entry;

        if (isShortPrintForm) {
            this.props.entryPage.isShortPrintForm = true;
        }
        app.isOnUpdate = true;
        entry.isViewMode = true;
        entry.isOnPrint = true;

        setTimeout(async () => {
            await Print.printContent({
                contentId: ENTRY_CONTENT_IN_VIEW_MODE_IDENTIFIER,
                onLoadingStart: this.addPrintClass,
                onLoadingEnd: this.removePrintClass,
            });
        });

        setTimeout(() => {
            entry.isViewMode = isViewMode;
            app.isOnUpdate = false;
            entry.isOnPrint = false;
            entryPage.isShortPrintForm = false;
        }, 1);
    }

    @action.bound
    private async toggleEDSPopup(): Promise<void> {
        this.isShowingEDSPopup = !this.isShowingEDSPopup;
    }

    @autobind
    private addPrintClass(): void {
        const el = document.getElementById(ENTRY_CONTENT_IN_VIEW_MODE_IDENTIFIER);
        assertIsDefined(el, PRINTED_ENTRY_CONTENT_MESSAGE);
        el.classList.add('print_root');
    }

    @autobind
    private removePrintClass(): void {
        const el = document.getElementById(ENTRY_CONTENT_IN_VIEW_MODE_IDENTIFIER);
        assertIsDefined(el, PRINTED_ENTRY_CONTENT_MESSAGE);
        el.classList.remove('print_root');
    }

    @autobind
    private closeRightMenu(): void {
        this.props.app.rightMenu.reset();
    }
}

export const ActionButtonContentContainer = withTranslation()(
    inject<ActionButtonProps, StoreProps>(...injectingStores)(Component),
);
